import {
  persistAvatars,
  getAvatarUrl,
  convertLocaleMobileNumToNumWithDialCode,
} from 'utils';
import { getTimeFromSeconds } from '../../utils/date';

const sortOperators = (operators) => {
  operators.sort((a, b) => {
    const x = a.accepted.totalAccepted;
    const y = b.accepted.totalAccepted;
    return y - x;
  });
  return operators;
};

const getOperators = (apiObject) => {
  return apiObject.map((agent) => {
    return {
      id: agent.id,
      fullName: agent.name,
      accessLevel: agent.type === '2' ? 'admin' : 'user',
      isCreator: agent.isCreator,
      position: agent.meta.title,
      email: agent.email,
      mobile: convertLocaleMobileNumToNumWithDialCode(agent.meta.phone),
      isOnline: agent.meta.online,
      workTime: agent.statistic
        ? getTimeFromSeconds(agent.statistic.totalTime)
        : 0,
      accepted: {
        totalAccepted: agent.statistic ? agent.statistic.totalAccepted : 0,
      },
      dailyStatistic: {},
      websiteIds:
        (agent.channels && agent.channels.map((channel) => channel.id)) || [],
      imageUrl: getAvatarUrl(agent.meta.avatar),
    };
  });
};

export const createOperatorsObject = (apiObject) => {
  persistAvatars(apiObject.map((agent) => agent.id));
  const operators = getOperators(apiObject);
  const sortedOperators = sortOperators(operators);
  return sortedOperators;
};

export const createOperatorsTableData = (operators) => {
  let adaptedOperators = [];
  operators.forEach((operator, index) => {
    let operatorData = {
      id: operator.id,
      name: operator.name,
      imageUrl: getAvatarUrl(operator.meta.avatar),
      totalWorkTime: operator.statistic
        ? getTimeFromSeconds(operator.statistic.totalTime)
        : 0,
      averageWorkTime: operator.statistic
        ? getTimeFromSeconds(
            Math.round(
              Number(operator.statistic.totalTime) /
                Number(operator.statistic.totalActivity)
            )
          )
        : 0,
      acceptedConversations: operator.statistic
        ? operator.statistic.totalAccepted
        : 0,
    };
    adaptedOperators.push(operatorData);
    operatorData = {};
  });

  return adaptedOperators;
};

export const createOperatorObject = ({
  operatorId,
  values,
  selectedAccessLevel,
  selectedWebsiteIds,
  imageUrl,
  isCreator,
  accepted,
  workTime,
}) => {
  return {
    id: operatorId,
    fullName: values.fullName,
    position: values.position,
    email: values.email,
    mobile: convertLocaleMobileNumToNumWithDialCode(values.mobile),
    accessLevel: selectedAccessLevel,
    isCreator,
    workTime,
    accepted,
    websiteIds: selectedWebsiteIds?.split(','),
    imageUrl:
      imageUrl === '/images/avatar-agent-default.svg' || !imageUrl
        ? ''
        : imageUrl,
  };
};

const getAllClosedAgentConvsCount = (conversations = []) => {
  let count = 0;

  conversations.forEach((conv) => {
    count += conv?.closedAgent ? +conv?.closedAgent : 0;
  });

  return count;
};

const getAllDeniedConvsCount = (conversations = []) => {
  let count = 0;

  conversations.forEach((conv) => {
    count += conv?.denied ? +conv?.denied : 0;
  });

  return count;
};

const getOperatorConversations = (conversations) => {
  let acceptedConversations = {};
  let closedConversations = {};
  let closedAgentConversations = {};
  let deniedConversations = {};

  conversations.map((item, index) => {
    acceptedConversations[item.day] = item.accepted;
    closedConversations[item.day] = item.closed;
    closedAgentConversations[item.day] = item.closedAgent;
    deniedConversations[item.day] = item.denied;
  });
  return {
    acceptedConversations,
    closedConversations,
    closedAgentConversations,
    deniedConversations,
  };
};

const getActivitiesChartData = (activities) => {
  let adaptedActivities = {};
  activities.map((item, index) => {
    adaptedActivities[item.day] = item.houres;
  });

  return adaptedActivities;
};

const getTotalActivityTime = (activities) => {
  let totalActivities = 0;
  activities.map((item, index) => {
    totalActivities += item.houres;
  });

  return totalActivities.toFixed(2);
};

export const createOperatorReportObject = (operatorReport) => {
  const adaptedOperatorReport = {
    name: operatorReport.agent.name,
    email: operatorReport.agent.email,
    avatar: operatorReport.agent.meta.avatar,
    position: operatorReport.agent.meta.title,
    acceptedConversations: operatorReport.acceptedConversations,
    closedConversations: operatorReport.closedConversations,
    closedAgentConversations: getAllClosedAgentConvsCount(operatorReport.days),
    deniedConversations: getAllDeniedConvsCount(operatorReport.days),
    endOnline: operatorReport.endOnline,
    startOnline: operatorReport.startOnline,
    conversationsChartData: getOperatorConversations(operatorReport.days),
    activitiesChartData: getActivitiesChartData(operatorReport.times),
    totalActivityTime: getTotalActivityTime(operatorReport.times),
    imageUrl: getAvatarUrl(operatorReport.agent.meta.avatar),
  };

  return adaptedOperatorReport;
};

// This func, convert operator rate to a number between 0-100
const getNormalizedRate = (rate) => {
  if (!!rate) return (((4 - rate) / 3) * 100).toFixed(2);
  return 0;
};

const compare = (a, b) => {
  if (a.score !== b.score) return b.score - a.score;
  /*
    If operators score was same, then compare them with below order :
      1.totalAccepted
      2.startAvg
      3.closeAvg
      4.ratePercent
  */
  if (a.totalAccepted !== b.totalAccepted)
    return b.totalAccepted - a.totalAccepted;
  if (a.startAvg !== b.startAvg) return a.startAvg - b.startAvg;
  if (a.closeAvg !== b.closeAvg) return a.closeAvg - b.closeAvg;

  if (a.ratePercent !== b.ratePercent) return b.ratePercent - a.ratePercent;

  return 0;
};

const getScore = (op, metrics) => {
  let score = 0;
  //? 25 points are considered for each metric

  if (!!op.startAvg && op.startAvg < metrics.startAvgTime) {
    score += 25;
  }
  if (!!op.closeAvg && op.closeAvg < metrics.closeAvgTime) {
    score += 25;
  }
  if (op.totalAccepted > metrics.totalAccepted) {
    score += 25;
  }
  if (op.ratePercent > +metrics.rateAvg) {
    score += 25;
  }

  return score;
};

export const getScoringMetrics = (data) => {
  const obj = {};

  const acceptAndCloseAvgRes = data?.acceptAndCloseAvgRes;
  const acceptAndDurationRes = data?.acceptAndDurationRes;
  const allAgentsRatesRes = data?.allAgentsRatesRes;

  // metrics

  // startAvg: average time to start conversation
  obj['startAvgTime'] = !!acceptAndCloseAvgRes?.length
    ? (acceptAndCloseAvgRes[0]?.startAvg / 1000).toFixed(2)
    : 0;
  // closeAvgTime: conversation average time from start to close
  obj['closeAvgTime'] = !!acceptAndCloseAvgRes?.length
    ? (acceptAndCloseAvgRes[0]?.closeAvg / 1000).toFixed(2)
    : 0;
  // totalAccepted: total accepted conversations
  obj['totalAccepted'] = !!acceptAndDurationRes?.length
    ? (
        acceptAndDurationRes[0]?.totalAccepted / data?.agentsRatesRes?.length
      ).toFixed(0)
    : 0;
  // rateAvg: customer success percent
  obj['rateAvg'] = !!allAgentsRatesRes?.length
    ? +getNormalizedRate(allAgentsRatesRes[0]?.average)
    : 0;

  return obj;
};

const getFormattedList = (data) => {
  const main = data?.agentsRatesRes;

  // We have two additional array that should be merged into main array
  const closeTimePerAgentRes = data?.acceptAndCloseTimePerAgentRes;
  const durationPerAgentRes = data?.acceptAndDurationPerAgentRes;

  return main.map((agent) => {
    const obj = { ...agent };

    const closeTimeItem = closeTimePerAgentRes.find((item) => {
      return item._id === obj._id;
    });
    const durationItem = durationPerAgentRes.find((item) => {
      return item._id === obj._id;
    });

    // ? The times that are comming from backend is in "ms"

    obj['totalTime'] = durationItem ? durationItem?.totalTime / 1000 : 0;
    obj['avgActivity'] = durationItem ? durationItem?.avgActivity / 1000 : 0;
    obj['totalAccepted'] = durationItem ? durationItem?.totalAccepted : 0;
    obj['startAvg'] = closeTimeItem ? closeTimeItem?.startAvg / 1000 : 0;
    obj['closeAvg'] = closeTimeItem ? closeTimeItem?.closeAvg / 1000 : 0;
    obj['ratePercent'] = +getNormalizedRate(+agent?.average);

    return obj;
  });
};

const getSortedOperators = (list = [], metrics) => {
  // Score to all operators based on metrics first
  const scoredList = list.map((item) => {
    const obj = { ...item };
    obj['score'] = getScore(obj, metrics);
    return obj;
  });
  // Sort them based on score
  scoredList.sort(compare);

  return scoredList;
};

export const getOperatosList = (data) => {
  const mainList = getFormattedList(data);
  const metrics = getScoringMetrics(data);

  return getSortedOperators(mainList, metrics);
};

export const getOperatorRemovedNotif = (fullName, locale) => {
  if (locale === 'en') {
    return `${fullName}  removed from the list of operators`;
  }

  return `${fullName} از ليست اپراتورها حذف شد.`;
};
